import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { Alert, Button, Container, Form } from 'reactstrap';

import FormFieldTextOrNumber from '../../../../../components/formFields/FormFieldTextOrNumber';
import FormFieldSwitchPill from '../../../../../components/formFields/FormFieldSwitchPill';

type SettingsSectionProperties =
{
    settings: any | null
    testResult: any | null
    lastError: string | null
    providerCredentials: any | null
    onSubmit: (values: any) => Promise<void> | void
    onTestConnection: (values: any) => Promise<any>
}

const defaultSettings =
{
    merchantAccountId: '',
    automaticSynchronizationEnabled: false
};

function ProviderCredentialsSection({ providerCredentials }: any)
{
    const message = providerCredentials?.configured
        ?
        (
            providerCredentials?.serviceAccountEmail
                ? <><strong>Service account:</strong> {providerCredentials.serviceAccountEmail}</>
                : 'Brak konfiguracji systemowej. Skontaktuj się z administratorem systemu.'
        )
        : 'Brak konfiguracji systemowej. Skontaktuj się z administratorem systemu.';

    return (
        <section className="mb-5">
            <h5 className="mb-3">Aplikacja Connectico w Google Merchant</h5>

            <div className="border rounded p-3">
                <div className="mt-2">
                    {message}
                </div>

                {providerCredentials?.serviceAccountEmail && (
                    <div className="mt-3 text-muted">
                        Dodaj powyższy adres jako użytkownika z dostępem Admin w Google Merchant Center.
                    </div>
                )}
            </div>
        </section>
    );
}

function SettingsSection
(
    {
        settings,
        testResult,
        lastError,
        providerCredentials,
        onSubmit,
        onTestConnection
    }
    : SettingsSectionProperties
)
{
    const [isTesting, setIsTesting] = useState(false);

    const initialValues =
    {
        ...defaultSettings,
        ...(settings ?? {})
    };

    const handleSubmitSettings = async (values: any) =>
    {
        await onSubmit
        ({
            ...(settings ?? {}),
            ...values,
            name: settings?.name || 'Google Merchant Center',
            syncPaused: settings?.syncPaused ?? false,
            pauseReason: settings?.pauseReason ?? null
        });
    };

    return (
        <Container fluid>
            <FinalForm
                initialValues={initialValues}
                onSubmit={handleSubmitSettings}
                render={({ handleSubmit, values, form, dirty }) =>
                {
                    const handleTestConnection = async () =>
                    {
                        setIsTesting(true);

                        try
                        {
                            await onTestConnection(values);
                        }
                        finally
                        {
                            setIsTesting(false);
                        }
                    };

                    return (
                        <Form onSubmit={handleSubmit}>
                            <section className="mb-5">
                                <div className="mb-4">
                                    <FormFieldSwitchPill
                                        name="automaticSynchronizationEnabled"
                                        label="Automatyczny eksport produktów"
                                        className="mr-5"
                                    />
                                </div>

                                {values.syncPaused && values.pauseReason &&
                                    <Alert color="warning" className="mt-3">
                                        {values.pauseReason}
                                    </Alert>
                                }

                                <div className="row">
                                    <div className="col-12 col-xl-6">
                                        <div className="mb-3">
                                            <FormFieldTextOrNumber name="merchantAccountId" label="Merchant Account ID" />
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <ProviderCredentialsSection providerCredentials={providerCredentials} />

                            <div className="d-flex flex-wrap justify-content-end mt-4">
                                <Button type="button" color="secondary" className="mr-2 mb-2" disabled={isTesting} onClick={handleTestConnection}>
                                    Test połączenia
                                </Button>
                                <Button type="submit" color="primary" className="mb-2">
                                    Zapisz konfigurację
                                </Button>
                            </div>

                            {lastError && <Alert color="danger" className="mt-3">{lastError}</Alert>}

                            {testResult && (
                                <Alert color={testResult.success ? 'success' : 'danger'} className="mt-3">
                                    <strong>{testResult.success ? 'Połączenie działa.' : 'Test połączenia nie powiódł się.'}</strong>

                                    {testResult.message && <div>{testResult.message}</div>}

                                    {testResult.warnings && testResult.warnings.length > 0 && (
                                        <ul className="mt-3 mb-0">
                                            {testResult.warnings.map((warning: string, index: number) => (
                                                <li key={index}>{warning}</li>
                                            ))}
                                        </ul>
                                    )}

                                    {testResult.account && (
                                        <pre className="mt-3 mb-0">{JSON.stringify(testResult.account, null, 2)}</pre>
                                    )}
                                </Alert>
                            )}
                        </Form>
                    );
                }}
            />
        </Container>
    );
}

export default SettingsSection;
